import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { Plus, Search, RefreshCw, Lightbulb, Grid3x3 as Grid3X3, Table2, CreditCard as Edit2, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import ThoughtModal from './ThoughtModal';
import { useAuth } from '../../context/AuthContext';
import { captureSupabaseFailure } from '../../sentry';

interface Thought {
  id: string;
  title: string;
  seed_idea: string;
  reference: string;
  status: string;
  final_copy: string;
  arc_slug: string | null;
  arc_stage: string | null;
  arc_placement: string | null;
  arc_fit_note: string | null;
  first_published_at: string | null;
  created_at: string;
  updated_at: string;
}

type ViewMode = 'cards' | 'table';

const ThoughtsView: React.FC = () => {
  const { user } = useAuth();
  const [thoughts, setThoughts] = useState<Thought[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentThought, setCurrentThought] = useState<Thought | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>('cards');

  const fetchThoughts = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('thoughts')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setThoughts(data || []);
    } catch (error: any) {
      captureSupabaseFailure('thoughts', 'list', error);
      toast.error(`Error fetching thoughts: ${error.message}`);
      console.error('Thoughts fetch error:', error);
      setThoughts([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchThoughts();
  }, [user]);

  const filteredThoughts = thoughts.filter((thought) => {
    const searchLower = searchQuery.toLowerCase();
    return (
      thought.title?.toLowerCase().includes(searchLower) ||
      thought.seed_idea?.toLowerCase().includes(searchLower) ||
      thought.arc_slug?.toLowerCase().includes(searchLower) ||
      thought.status?.toLowerCase().includes(searchLower)
    );
  });

  const handleAddThought = () => {
    setCurrentThought(null);
    setIsModalOpen(true);
  };

  const handleEditThought = (thought: Thought) => {
    setCurrentThought(thought);
    setIsModalOpen(true);
  };

  const handleDeleteThought = async (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!confirm('Delete this idea?')) return;
    try {
      const { error } = await supabase.from('thoughts').delete().eq('id', id);
      if (error) throw error;
      toast.success('Thought deleted');
      void fetchThoughts();
    } catch (error: any) {
      captureSupabaseFailure('thoughts', 'delete', error, { thought_id: id });
      toast.error(`Error deleting thought: ${error.message}`);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'promoted':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
      case 'completed':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'in_progress':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
      case 'dead':
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200';
      default:
        return 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white flex items-center gap-2">
            <Lightbulb className="w-6 h-6 text-amber-500" />
            Ideas
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Content ideas — place on narrative arcs, then stub content on the timeline
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="inline-flex items-center gap-0.5 bg-gray-100 dark:bg-gray-900 rounded-lg p-0.5">
            <button
              type="button"
              onClick={() => setViewMode('cards')}
              className={`p-1.5 rounded-md ${viewMode === 'cards' ? 'bg-white dark:bg-gray-700 shadow-sm' : ''}`}
              aria-label="Card view"
            >
              <Grid3X3 className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={() => setViewMode('table')}
              className={`p-1.5 rounded-md ${viewMode === 'table' ? 'bg-white dark:bg-gray-700 shadow-sm' : ''}`}
              aria-label="Table view"
            >
              <Table2 className="w-4 h-4" />
            </button>
          </div>
          <button
            type="button"
            onClick={handleAddThought}
            className="inline-flex items-center gap-1.5 px-3 py-2 text-sm rounded-md bg-blue-600 hover:bg-blue-700 text-white"
          >
            <Plus className="w-4 h-4" />
            New idea
          </button>
        </div>
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search ideas…"
          className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-900"
        />
      </div>

      {viewMode === 'cards' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {loading ? (
            <div className="col-span-full flex justify-center py-12">
              <RefreshCw className="w-6 h-6 animate-spin text-purple-500" />
            </div>
          ) : filteredThoughts.length === 0 ? (
            <div className="col-span-full text-center py-12 text-gray-500 dark:text-gray-400">
              No ideas found
            </div>
          ) : (
            filteredThoughts.map((thought) => (
              <div
                key={thought.id}
                className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 hover:shadow-md transition-shadow cursor-pointer"
                onClick={() => handleEditThought(thought)}
              >
                <div className="flex items-start justify-between mb-2">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                    {thought.title || 'Untitled'}
                  </h3>
                  {thought.status && (
                    <span
                      className={`inline-flex items-center px-2 py-1 rounded-full text-xs ${getStatusColor(thought.status)}`}
                    >
                      {thought.status}
                    </span>
                  )}
                </div>
                {thought.seed_idea && (
                  <p className="text-gray-500 dark:text-gray-400 text-sm line-clamp-3 mb-4">
                    {thought.seed_idea}
                  </p>
                )}
                {thought.arc_slug && (
                  <div className="mb-2">
                    <span className="text-xs font-medium text-purple-600 dark:text-purple-400">
                      Arc: {thought.arc_slug}
                      {thought.arc_stage ? ` · ${thought.arc_stage}` : ''}
                      {thought.arc_placement ? ` · ${thought.arc_placement}` : ''}
                    </span>
                  </div>
                )}
                <div className="flex items-center text-sm text-gray-500 dark:text-gray-400">
                  {formatDate(thought.created_at)}
                </div>
              </div>
            ))
          )}
        </div>
      ) : (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Title
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Arc
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {loading ? (
                  <tr>
                    <td colSpan={5} className="px-6 py-4 text-center">
                      <RefreshCw className="w-6 h-6 animate-spin text-purple-500 mx-auto" />
                    </td>
                  </tr>
                ) : filteredThoughts.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-6 py-4 text-center text-gray-500 dark:text-gray-400">
                      No thoughts found
                    </td>
                  </tr>
                ) : (
                  filteredThoughts.map((thought) => (
                    <tr
                      key={thought.id}
                      className="hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer"
                      onClick={() => handleEditThought(thought)}
                    >
                      <td className="px-6 py-4">
                        <div className="text-sm font-medium text-gray-900 dark:text-white">
                          {thought.title || 'Untitled'}
                        </div>
                        {thought.seed_idea && (
                          <div className="text-sm text-gray-500 dark:text-gray-400 line-clamp-1">
                            {thought.seed_idea}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900 dark:text-white">
                          {thought.arc_slug
                            ? `${thought.arc_slug}${thought.arc_stage ? ` · ${thought.arc_stage}` : ''}`
                            : '—'}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        {thought.status && (
                          <span
                            className={`inline-flex items-center px-2 py-1 rounded-full text-xs ${getStatusColor(thought.status)}`}
                          >
                            {thought.status}
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900 dark:text-white">
                          {formatDate(thought.created_at)}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEditThought(thought);
                            }}
                            className="p-1.5 text-gray-500 hover:text-blue-600"
                            aria-label="Edit"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={(e) => void handleDeleteThought(thought.id, e)}
                            className="p-1.5 text-gray-500 hover:text-red-600"
                            aria-label="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <ThoughtModal
        isOpen={isModalOpen}
        thought={currentThought}
        onClose={() => {
          setIsModalOpen(false);
          setCurrentThought(null);
        }}
        onSuccess={() => void fetchThoughts()}
      />
    </div>
  );
};

export default ThoughtsView;
